import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {format} from 'date-fns';
import {productionService, productService} from '../../services/api';
import SearchableSelect from "../../components/ui/SearchableSelect";
import { toast } from 'react-toastify';

const ProductionBatchForm = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const isEditing = !!id;

	// Form state
	const [formData, setFormData] = useState({
		                                         planned_completion_date: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 1 week from now
		                                         notes: '',
		                                         items: [
			                                         {
				                                         product_id: '',
				                                         quantity: 1,
				                                         variant: 'standard', // Default variant
				                                         status: 'pending',
				                                         hasMissingParts: false,
				                                         missingPartsDetails: []
			                                         }
		                                         ]
	                                         });

	// Status mapping
	const statusMapping = {
		'PLANNED': 'planned',
		'IN_PROGRESS': 'in_progress',
		'COMPLETED': 'completed',
		'CANCELLED': 'cancelled'
	};

	const reverseStatusMapping = {
		'planned': 'PLANNED',
		'in_progress': 'IN_PROGRESS',
		'completed': 'COMPLETED',
		'cancelled': 'CANCELLED'
	};

	// Reference data
	const [products, setProducts] = useState([]);
	const [variantOptions, setVariantOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [submitLoading, setSubmitLoading] = useState(false);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [hasAnyMissingParts, setHasAnyMissingParts] = useState(false);
	const [itemsLoading, setItemsLoading] = useState([]);
	const [tempDate, setTempDate] = useState(''); // New state for temporary date storage

	useEffect(() => {
		const loadData = async () => {
			setLoading(true);
			try {
				// Load products
				const productsData = await productService.getProducts();
				setProducts(productsData.data);

				const variantOptionData = await productService.getVariantOptions();
				setVariantOptions(variantOptionData);

				// If editing, load the batch
				if (isEditing) {
					const batchData = await productionService.getProductionBatch(id);

					// Transform backend data to frontend format with missing parts properties
					const itemsWithMissingParts = batchData.items.map(item => ({
						...item,
						hasMissingParts: false,
						missingPartsDetails: []
					}));

					setFormData({
									batch_number: batchData.batch_number,
						            planned_completion_date: batchData.planned_completion_date,
						            actual_completion_date: batchData.actual_completion_date || null,
						            notes: batchData.notes || '',
						            status: batchData.status,
						            items: itemsWithMissingParts || []
					            });

					// Initialize tempDate with the loaded planned_completion_date
					setTempDate(batchData.planned_completion_date);

					// Check for missing parts in each item
					const loadingFlags = itemsWithMissingParts.map(() => false);
					setItemsLoading(loadingFlags);

					for (let i = 0; i < itemsWithMissingParts.length; i++) {
						const item = itemsWithMissingParts[i];
						if (item.product_id && item.quantity > 0) {
							await checkItemParts(i, item.product_id, item.quantity, item.variant);
						}
					}
				} else {
					// Set the initial tempDate for new forms
					setTempDate(formData.planned_completion_date);
				}
			}
			catch (error) {
				console.error("Failed to load data:", error);
				// Alert or toast notification could go here
			}
			finally {
				setLoading(false);
			}
		};

		loadData();
	}, [id, isEditing]);

	// Check if any items have missing parts after form data updates
	useEffect(() => {
		const anyMissingParts = formData.items.some(item => item.hasMissingParts);
		setHasAnyMissingParts(anyMissingParts);
	}, [formData.items]);

	const handleInputChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	// Updated date handling functions
	const handleDateChange = (e) => {
		setTempDate(e.target.value);
	};

	const applyDateChange = () => {
		if (tempDate) {
			setFormData(prev => ({ ...prev, planned_completion_date: tempDate }));
		}
		setShowDatePicker(false);
	};

	const handleItemChange = async (index, field, value) => {
		const updatedItems = [...formData.items];
		updatedItems[index] = { ...updatedItems[index], [field]: value };
		setFormData(prev => ({ ...prev, items: updatedItems }));

		// Check for missing parts when product or quantity changes
		if ((field === 'product_id' || field === 'quantity' || field === 'variant') &&
		    updatedItems[index].product_id &&
		    updatedItems[index].quantity > 0) {
			await checkItemParts(
				index,
				updatedItems[index].product_id,
				updatedItems[index].quantity,
				updatedItems[index].variant
			);
		}
	};

	const checkItemParts = async (index, productId, quantity, variant) => {
		// Set loading state for this specific item
		const newLoadingFlags = [...itemsLoading];
		newLoadingFlags[index] = true;
		setItemsLoading(newLoadingFlags);

		try {
			const result = await productionService.checkProductParts(productId, quantity, variant);

			// Use the functional update pattern to ensure we're working with the latest state
			setFormData(prev => {
				const updatedItems = [...prev.items];
				updatedItems[index] = {
					...updatedItems[index],
					product_id: productId, // Explicitly set the product_id
					quantity: quantity,    // Explicitly set the quantity
					variant: variant,      // Explicitly set the variant
					hasMissingParts: result.has_missing_parts,
					missingPartsDetails: result.missing_parts_details || []
				};
				return { ...prev, items: updatedItems };
			});
		}
		catch (error) {
			console.error(`Error checking parts for item ${index}:`, error);
			// You could add an error state to the item here if needed
		}
		finally {
			// Clear loading state for this item
			const updatedLoadingFlags = [...itemsLoading];
			updatedLoadingFlags[index] = false;
			setItemsLoading(updatedLoadingFlags);
		}
	};

	const addItem = () => {
		setFormData(prev => ({
			...prev,
			items: [
				...prev.items,
				{
					product_id: '',
					quantity: 1,
					variant: 'standard',
					status: 'pending',
					hasMissingParts: false,
					missingPartsDetails: []
				}
			]
		}));

		// Add a loading flag for the new item
		setItemsLoading(prev => [...prev, false]);
	};

	const removeItem = (index) => {
		if (formData.items.length <= 1) {
			return; // Keep at least one item
		}

		const updatedItems = [...formData.items];
		updatedItems.splice(index, 1);
		setFormData(prev => ({ ...prev, items: updatedItems }));

		// Remove the loading flag for this item
		const updatedLoadingFlags = [...itemsLoading];
		updatedLoadingFlags.splice(index, 1);
		setItemsLoading(updatedLoadingFlags);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Prevent submission if there are missing parts
		if (hasAnyMissingParts && !isEditing) {
			alert("Cannot create batch with missing parts. Please adjust quantities or select different products.");
			return;
		}

		setSubmitLoading(true);

		try {
			// Validate form
			const hasEmptyProduct = formData.items.some(item => !item.product_id);
			if (hasEmptyProduct) {
				throw new Error('Please select a product for all items');
			}

			const hasInvalidQuantity = formData.items.some(item => item.quantity <= 0);
			if (hasInvalidQuantity) {
				throw new Error('Quantity must be greater than zero for all items');
			}

			// Convert any empty string quantities to 1 before submission
			const formattedData = {
				...formData,
				items: formData.items.map(item => {
					// Remove the missing parts properties before sending to API
					const { hasMissingParts, missingPartsDetails, ...cleanItem } = item;
					return {
						...cleanItem,
						quantity: item.quantity === '' ? 1 : item.quantity
					};
				})
			};

			// Prepare data for submission
			const batchData = {
				...formattedData,
				// If editing and there's a status, include it, otherwise omit
				...(isEditing && formattedData.status ? { status: formattedData.status } : {})
			};

			// Submit form
			if (isEditing) {
				await productionService.updateProductionBatch(id, batchData);
				// Success notification
			} else {
				await productionService.createProductionBatch(batchData);
				// Success notification
			}

			// Navigate back to production list
			navigate('/production');
		}
		catch (error) {
			console.error("Error saving production batch:", error);

			// Check if it's a validation error response
			if (error.response?.data?.errors) {
				// Get the first error message from the errors object
				const errors = error.response.data.errors;
				const firstErrorKey = Object.keys(errors)[0];
				const firstErrorMessage = errors[firstErrorKey][0];

				toast.error(firstErrorMessage);

				// Optionally, show all errors
				// Object.keys(errors).forEach(key => {
				//     errors[key].forEach(message => {
				//         toast.error(message);
				//     });
				// });
			} else if (error.response?.data?.message) {
				// Handle general error messages
				toast.error(error.response.data.message);
			} else if (error.message) {
				// Handle network or other errors
				toast.error(error.message);
			} else {
				// Fallback error message
				toast.error("Error saving production batch");
			}
		}
		finally {
			setSubmitLoading(false);
		}
	};

	if (loading) {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-6">
			<div className="bg-white rounded-lg shadow">
				<div className="px-6 py-4 border-b border-gray-200">
					<h2 className="text-xl font-semibold text-gray-800">
						{isEditing ? 'Edit Production Batch' : 'Create New Production Batch'}
					</h2>
				</div>
				<form onSubmit={handleSubmit}>
					<div className="p-6 space-y-6">
						{isEditing && (
							<div className="space-y-2">
								<label htmlFor="status" className="block text-sm font-medium text-gray-700">
									Batch Number
								</label>
								<input
									id="batch_number"
									className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
									value={formData.batch_number}
									onChange={(e) => handleInputChange('batch_number', e.target.value)}
								/>
							</div>
						)}
						{/* Batch Information */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div className="space-y-2">
								<label className="block text-sm font-medium text-gray-700">
									Planned Completion Date
								</label>
								<div className="relative">
									<button
										type="button"
										className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
										onClick={() => {
											setTempDate(formData.planned_completion_date);
											setShowDatePicker(!showDatePicker);
										}}
									>
                                        <span className="flex items-center">
                                          <svg className="h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                                          </svg>
	                                        {format(new Date(formData.planned_completion_date + 'T12:00:00'), 'MMMM d, yyyy')}

                                        </span>
									</button>
									{showDatePicker && (
										<div className="absolute z-10 mt-1 bg-white rounded-md shadow-lg p-4 border border-gray-200">
											<div className="text-center mb-2">
												<input
													type="date"
													className="px-2 py-1 border border-gray-300 rounded"
													value={tempDate}
													onChange={handleDateChange}
												/>
											</div>
											<button
												type="button"
												className="w-full px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
												onClick={applyDateChange}
											>
												Done
											</button>
										</div>
									)}
								</div>
							</div>

							{isEditing && (
								<div className="space-y-2">
									<label htmlFor="status" className="block text-sm font-medium text-gray-700">
										Status
									</label>
									<select
										id="status"
										className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
										value={formData.status}
										onChange={(e) => handleInputChange('status', e.target.value)}
									>
										<option value="planned">Planned</option>
										<option value="in_progress">In Progress</option>
										<option value="completed">Completed</option>
									</select>
								</div>
							)}
						</div>

						{/* Notes */}
						<div className="space-y-2">
							<label htmlFor="notes" className="block text-sm font-medium text-gray-700">
								Notes
							</label>
							<textarea
								id="notes"
								rows={3}
								className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
								value={formData.notes}
								onChange={(e) => handleInputChange('notes', e.target.value)}
								placeholder="Add any production notes or special instructions here..."
							/>
						</div>

						{/* Production Items */}
						<div className="space-y-4">
							<div className="flex justify-between items-center">
								<h3 className="text-lg font-medium text-gray-900">Production Items</h3>
							</div>

							{formData.items.map((item, index) => (
								<div key={index} className={`bg-gray-50 p-4 rounded-md border ${item.hasMissingParts ? 'border-red-300' : 'border-gray-200'}`}>
									<div className="flex justify-between mb-4">
										<h4 className="text-md font-medium">Item #{index + 1}</h4>
										<button
											type="button"
											className="text-red-600 hover:text-red-800"
											onClick={() => removeItem(index)}
										>
											Remove
										</button>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Product
											</label>
											<SearchableSelect
												options={products.map(product => ({
													id: product.id,
													label: `${product.sku} ${product.description ? '-' + product.description : ''}`
												}))}
												value={item.product_id}
												onChange={(value) => handleItemChange(index, 'product_id', value)}
												placeholder="Select a product"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Quantity
											</label>
											<input
												type="number"
												min="1"
												className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
												value={item.quantity}
												onChange={(e) => {
													const value = e.target.value;
													if (value === '') {
														handleItemChange(index, 'quantity', '');
													} else {
														const parsedValue = parseInt(value, 10);
														handleItemChange(index, 'quantity', isNaN(parsedValue) ? '' : parsedValue);
													}
												}}
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Variant
											</label>
											<select
												className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
												value={item.variant}
												onChange={(e) => handleItemChange(index, 'variant', e.target.value)}
											>
												{variantOptions.map(variant => (
													<option key={variant.variant} value={variant.variant}>
														{variant.variant}
													</option>
												))}

											</select>
										</div>

										{isEditing && (
											<div>
												<label className="block text-sm font-medium text-gray-700 mb-1">
													Status
												</label>
												<select
													className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
													value={item.status}
													onChange={(e) => handleItemChange(index, 'status', e.target.value)}
												>
													<option value="pending">Pending</option>
													<option value="materials_allocated">Materials Allocated</option>
													<option value="in_production">In Production</option>
													<option value="completed">Completed</option>
												</select>
											</div>
										)}
									</div>

									{/* Missing Parts Warning */}
									{itemsLoading[index] && (
										<div className="mt-3 flex items-center text-gray-500">
											<svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
												<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
											</svg>
											Checking parts availability...
										</div>
									)}

									{!itemsLoading[index] && item.hasMissingParts && (
										<div className="mt-3">
											<div className="text-red-600 font-medium mb-1">
												Missing parts! Cannot produce this item with current inventory.
											</div>
											<div className="bg-red-50 border border-red-100 rounded-md p-2">
												<table className="min-w-full text-sm">
													<thead>
													<tr>
														<th className="text-left font-medium text-gray-700 pr-4">Part</th>
														<th className="text-right font-medium text-gray-700 pr-4">Required</th>
														<th className="text-right font-medium text-gray-700 pr-4">Available</th>
														<th className="text-right font-medium text-gray-700">Shortage</th>
													</tr>
													</thead>
													<tbody>
													{item.missingPartsDetails.map((part, partIndex) => (
														<tr key={partIndex}>
															<td className="pr-4">{part.name}</td>
															<td className="text-right pr-4">{part.required}</td>
															<td className="text-right pr-4">{part.available_total}</td>
															<td className="text-right font-medium text-red-600">{part.shortage}</td>
														</tr>
													))}
													</tbody>
												</table>
											</div>
										</div>
									)}
								</div>
							))}
							<div className="flex justify-end items-center">
								<button
									type="button"
									className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
									onClick={addItem}
								>
									Add Item
								</button>
							</div>

						</div>

						{/* Global missing parts warning */}
						{hasAnyMissingParts && (
							<div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
								<div className="flex">
									<svg className="h-5 w-5 text-red-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
										<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
									</svg>
									<div>
										<h3 className="text-sm font-medium">Insufficient inventory</h3>
										<div className="mt-1 text-sm">
											This batch cannot be created because one or more items have missing parts.
											Please adjust quantities or select different products.
										</div>
									</div>
								</div>
							</div>
						)}
					</div>

					<div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
						<button
							type="button"
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
							onClick={() => navigate('/production')}
						>
							Cancel
						</button>
						<button
							type="submit"
							className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${(((submitLoading || hasAnyMissingParts) && !isEditing) || (submitLoading)) ? 'opacity-70 cursor-not-allowed' : ''}`}
							disabled={((submitLoading || hasAnyMissingParts) && !isEditing) || (submitLoading)}
						>
							{submitLoading ? 'Saving...' : isEditing ? 'Update Batch' : 'Create Batch'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default ProductionBatchForm;
